import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { message } from "antd";
import dayjs from "dayjs";

// Types
export interface Staff {
  id: number;
  name: string;
  email: string;
  user_type_id: number;
  location_id: number;
  user_type: {
    id: number;
    name: string;
    icon: string;
    description: string;
  };
  location: {
    id: number;
    name: string;
    code: string;
    address: string;
  };
}
export interface Table {
  number: string;
}

export interface Schedule {
  id: number;
  user_id: number;
  table_id: number | null;
  schedule_date: string;
  start_time: string;
  end_time: string;
  shift_type: "morning" | "day" | "night";
  status: "scheduled" | "completed" | "canceled";
  notes: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  user: Staff;
  table: Table;
}

// Old payload for editing individual schedules
export interface SchedulePayload {
  user_id: number;
  table_id?: number | null;
  schedule_date: string;
  start_time: string;
  end_time: string;
  shift_type: "morning" | "day" | "night";
  status: "scheduled" | "completed" | "canceled";
  notes?: string;
}

// New payload for bulk schedule creation
export interface BulkSchedulePayload {
  user_id: number[];
  table_id: number[];
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  shift_type: "morning" | "day" | "night";
  status: "scheduled" | "completed" | "canceled";
  notes?: string;
}

// Helper function to format the date as YYYY-MM-DD for the API
const formatDateForApi = (dateString: string): string => {
  return dayjs(dateString).format("YYYY-MM-DD");
};

// Helper function to format time as HH:mm for the API
const formatTimeForApi = (timeString: string): string => {
  return dayjs(timeString, "HH:mm:ss").format("HH:mm");
};

// ✅ Fetch Schedules for a specific date range (much more efficient)
export const useSchedulesForDateRange = (
  startDate: string, // YYYY-MM-DD format
  endDate: string, // YYYY-MM-DD format
  filters: Record<string, any> = {}
) => {
  return useQuery({
    queryKey: ["schedules", "dateRange", startDate, endDate, filters],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      });

      // Add other filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          if (Array.isArray(value)) {
            value.forEach((v) => queryParams.append(key + "[]", v.toString()));
          } else {
            queryParams.set(key, value.toString());
          }
        }
      });

      console.log(`Fetching schedules for ${startDate} to ${endDate}`, {
        queryParams: queryParams.toString(),
      });

      const response = await apiClient.get(
        `/schedule?${queryParams.toString()}`
      );
      console.log(
        `Fetched ${response.data.data?.length || 0} schedules for date range`
      );

      return {
        data: response.data.data || [],
        total: response.data.data?.length || 0,
        dateRange: { startDate, endDate },
      };
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });
};

// ✅ Fetch All Schedules by fetching all pages (keep for admin/reporting use)
export const useAllSchedules = () => {
  return useQuery({
    queryKey: ["schedules", "all"],
    queryFn: async () => {
      let allSchedules: Schedule[] = [];
      let currentPage = 1;
      let hasMorePages = true;
      const maxPages = 100; // Safety limit to prevent infinite loops

      console.log("Starting to fetch all schedules...");

      while (hasMorePages && currentPage <= maxPages) {
        try {
          const response = await apiClient.get(`/schedule?page=${currentPage}`);
          const data = response.data;

          console.log(`Fetched page ${currentPage}:`, {
            currentPageData: data.data?.length || 0,
            totalSoFar: allSchedules.length,
            currentPage: data.meta?.current_page,
            lastPage: data.meta?.last_page,
            total: data.meta?.total,
            perPage: data.meta?.per_page,
          });

          // Validate response structure
          if (!data.data || !Array.isArray(data.data)) {
            console.error(
              `Invalid response structure on page ${currentPage}:`,
              data
            );
            break;
          }

          // Add schedules from this page
          allSchedules = [...allSchedules, ...data.data];

          // Check if there are more pages - using meta object
          if (
            data.meta &&
            typeof data.meta.current_page === "number" &&
            typeof data.meta.last_page === "number"
          ) {
            hasMorePages = data.meta.current_page < data.meta.last_page;

            // Double-check we're making progress
            if (data.meta.current_page !== currentPage) {
              console.warn(
                `Page mismatch: requested ${currentPage}, got ${data.meta.current_page}`
              );
            }
          } else {
            console.error(
              "Missing or invalid meta pagination data:",
              data.meta
            );
            break;
          }

          currentPage++;

          // Add a small delay to avoid overwhelming the API
          if (hasMorePages) {
            await new Promise((resolve) => setTimeout(resolve, 50));
          }
        } catch (error) {
          console.error(`Error fetching page ${currentPage}:`, error);
          // If we have some data, continue with what we have
          if (allSchedules.length > 0) {
            console.log(
              `Continuing with ${allSchedules.length} schedules despite error on page ${currentPage}`
            );
            break;
          } else {
            // If no data yet, re-throw the error
            throw error;
          }
        }
      }

      if (currentPage > maxPages) {
        console.warn(
          `Stopped at max pages limit (${maxPages}). You may need to increase the limit.`
        );
      }

      console.log(
        `✅ Finished fetching schedules. Total: ${allSchedules.length} from ${
          currentPage - 1
        } pages`
      );

      return {
        data: allSchedules,
        total: allSchedules.length,
        pagesLoaded: currentPage - 1,
      };
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: false,
    retry: 2, // Retry failed requests up to 2 times
    enabled: false, // Disable by default - only use when specifically needed
  });
};

// ✅ Keep original for backward compatibility - fetch with pagination like products
export const useSchedules = (
  page: number = 1,
  filters: Record<string, any> = {}
) => {
  return useQuery({
    queryKey: ["schedules", page, filters],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== null && v !== "")
        ), // Exclude null/empty filters
      }).toString();

      const response = await apiClient.get<{
        data: Schedule[];
        meta: {
          total: number;
          current_page: number;
          last_page: number;
          per_page: number;
          from: number;
          to: number;
        };
        links: {
          first: string;
          last: string;
          prev: string | null;
          next: string | null;
        };
      }>(`/schedule?${queryParams}`);

      console.log(`Fetched schedules page ${page}:`, {
        currentPageData: response.data.data?.length || 0,
        currentPage: response.data.meta?.current_page,
        lastPage: response.data.meta?.last_page,
        total: response.data.meta?.total,
      });

      return response.data;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Get a specific schedule by ID
export const useScheduleById = (id: number) => {
  return useQuery({
    queryKey: ["schedule", id],
    queryFn: async () => {
      const response = await apiClient.get(`/schedule/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

// Create new schedules (bulk creation with new API)
export const useCreateSchedule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: BulkSchedulePayload) => {
      // Format the data according to the new API requirements
      const formattedPayload = {
        ...payload,
        start_date: formatDateForApi(payload.start_date),
        end_date: formatDateForApi(payload.end_date),
        start_time: formatTimeForApi(payload.start_time),
        end_time: formatTimeForApi(payload.end_time),
      };

      console.log("Sending bulk schedule payload:", formattedPayload);

      // Use the bulk creation endpoint (update this endpoint as needed)
      const response = await apiClient.post(
        "/schedule/create", // Update this to match your actual bulk endpoint
        formattedPayload
      );
      return response.data;
    },
    onSuccess: (data) => {
      console.log("Bulk schedule creation successful:", data);
      // Invalidate both schedule queries
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
    },
    onError: (error) => {
      console.error("Bulk schedule creation error:", error);
    },
  });
};

// Update an existing schedule (keep for editing individual schedules)
export const useUpdateSchedule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: Partial<SchedulePayload>;
    }) => {
      // Format date and time fields if they exist in the payload
      const formattedPayload = {
        ...payload,
        ...(payload.schedule_date && {
          schedule_date: formatDateForApi(payload.schedule_date),
        }),
        ...(payload.start_time && {
          start_time: formatTimeForApi(payload.start_time),
        }),
        ...(payload.end_time && {
          end_time: formatTimeForApi(payload.end_time),
        }),
      };

      // Use the direct ID endpoint for updating
      const response = await apiClient.put(`/schedule/${id}`, formattedPayload);
      return response.data;
    },
    onSuccess: (_, variables) => {
      message.success("Schedule updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
      queryClient.invalidateQueries({ queryKey: ["schedule", variables.id] });
    },
    onError: (error) => {
      console.error("Schedule update error:", error);
      message.error("Failed to update schedule.");
    },
  });
};

// Delete a schedule
export const useDeleteSchedule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await apiClient.delete(`/schedule/${id}`);
      return response.data;
    },
    onSuccess: () => {
      message.success("Schedule deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
    },
    onError: (error) => {
      console.error("Schedule deletion error:", error);
      message.error("Failed to delete schedule.");
    },
  });
};

// Fetch staff users from user_type=2 (Staff) - Follow products pattern
export const useStaff = (locationId?: number) => {
  return useQuery({
    queryKey: ["staff", locationId],
    queryFn: async () => {
      // Construct URL with optional location filter - no per_page parameter
      const queryParams = new URLSearchParams();
      queryParams.set("user_type_id", "2");

      if (locationId) {
        queryParams.set("location_id", locationId.toString());
      }

      const response = await apiClient.get(`/user?${queryParams.toString()}`);
      console.log("Fetched staff:", response.data);
      return response.data;
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    refetchOnWindowFocus: false,
  });
};

// Fetch all locations - Follow products pattern
export const useLocations = () => {
  return useQuery({
    queryKey: ["locations"],
    queryFn: async () => {
      const response = await apiClient.get<{
        status: boolean;
        data: Location[];
      }>("/location");
      console.log("Fetched locations:", response.data);
      return response.data.data; // ✅ Extract location data
    },
    staleTime: 1000 * 60 * 60, // ✅ Cache for 1 hour
    refetchOnWindowFocus: false,
  });
};
